<template>
  <list-page-shell>
    <template #actions>
      <el-button v-permission="['SYSTEM_USER_CREATE']" type="primary" size="small" @click="openCreate">{{ $t('systemUsersPage.create') }}</el-button>
      <el-button size="small" plain @click="load">{{ $t('common.refresh') }}</el-button>
    </template>

    <template #filters>
      <el-form inline size="small" class="filter-form">
        <el-form-item :label="$t('common.keyword')">
          <el-input v-model.trim="query.keyword" :placeholder="$t('systemUsersPage.keywordPh')" clearable @keyup.enter.native="handleSearch" />
        </el-form-item>
        <el-form-item :label="$t('common.status')">
          <el-select v-model="query.statusCode" clearable :placeholder="$t('common.all')" style="width: 120px;">
            <el-option :value="1" :label="$t('common.active')" />
            <el-option :value="2" :label="$t('common.inactive')" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

    <el-table v-loading="loading" :data="rows" size="small" border>
      <el-table-column prop="username" :label="$t('grid.username')" width="150" />
      <el-table-column prop="realName" :label="$t('grid.realName')" width="140" />
      <el-table-column prop="email" :label="$t('grid.email')" min-width="180" show-overflow-tooltip />
      <el-table-column prop="phone" :label="$t('grid.phone')" width="140" />
      <el-table-column :label="$t('grid.roles')" min-width="180" show-overflow-tooltip>
        <template slot-scope="scope">{{ roleText(scope.row) }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.status')" width="110" align="center">
        <template slot-scope="scope">
          <el-switch
            v-permission="['SYSTEM_USER_STATUS']"
            :value="scope.row.statusCode"
            :active-value="1"
            :inactive-value="2"
            @change="toggleStatus(scope.row, $event)"
          />
          <el-tag v-if="!hasPermission('SYSTEM_USER_STATUS')" size="mini" :type="scope.row.statusCode === 1 ? 'success' : 'info'">
            {{ scope.row.statusCode === 1 ? $t('common.active') : $t('common.inactive') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="$t('grid.createdAt')" width="210">
        <template slot-scope="scope">{{ formatDisplayDateTime(scope.row.createdAt) || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.actions')" width="280" fixed="right">
        <template slot-scope="scope">
          <el-button v-permission="['SYSTEM_USER_EDIT']" type="text" size="small" @click="openEdit(scope.row)">{{ $t('common.edit') }}</el-button>
          <el-button v-permission="['SYSTEM_USER_ASSIGN_ROLE']" type="text" size="small" @click="openRoleDialog(scope.row)">{{ $t('systemUsersPage.assignRoles') }}</el-button>
          <el-button v-permission="['SYSTEM_USER_RESET_PASSWORD']" type="text" size="small" @click="openPasswordDialog(scope.row)">{{ $t('systemUsersPage.resetPassword') }}</el-button>
          <el-button v-permission="['SYSTEM_USER_DELETE']" type="text" size="small" class="danger-text" @click="handleDelete(scope.row)">{{ $t('common.delete') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="query.pageSize"
        :current-page.sync="query.pageNo"
        @current-change="load"
      />
    </div>

    <form-dialog :title="editingId ? $t('systemUsersPage.editUser') : $t('systemUsersPage.newUser')" :visible.sync="editDialogVisible" width="520px">
      <el-form ref="userForm" :model="userForm" :rules="userRules" label-width="90px">
        <el-form-item :label="$t('grid.username')" prop="username">
          <el-input v-model.trim="userForm.username" maxlength="64" />
        </el-form-item>
        <el-form-item v-if="!editingId" :label="$t('common.password')" prop="password">
          <el-input v-model.trim="userForm.password" show-password :placeholder="$t('systemUsersPage.passwordPh')" />
        </el-form-item>
        <el-form-item :label="$t('grid.realName')" prop="realName">
          <el-input v-model.trim="userForm.realName" maxlength="64" />
        </el-form-item>
        <el-form-item :label="$t('grid.email')">
          <el-input v-model.trim="userForm.email" maxlength="128" />
        </el-form-item>
        <el-form-item :label="$t('grid.phone')">
          <el-input v-model.trim="userForm.phone" maxlength="32" />
        </el-form-item>
        <el-form-item :label="$t('common.status')">
          <el-radio-group v-model="userForm.statusCode">
            <el-radio :label="1">{{ $t('common.active') }}</el-radio>
            <el-radio :label="2">{{ $t('common.inactive') }}</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="saveUser">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>

    <form-dialog :title="$t('systemUsersPage.assignRoles')" :visible.sync="roleDialogVisible" width="520px">
      <div v-if="currentRow" class="dialog-tip">{{ $t('systemUsersPage.currentUser') }}：{{ currentRow.username }}{{ currentRow.realName ? ` / ${currentRow.realName}` : '' }}</div>
      <el-checkbox-group v-model="selectedRoleIds" class="role-check-group">
        <el-checkbox v-for="item in roleOptions" :key="item.id" :label="item.id">
          {{ item.roleName }}<span class="role-code">({{ item.roleCode }})</span>
        </el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="roleDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="roleSaving" @click="saveRoles">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>

    <form-dialog :title="$t('systemUsersPage.resetPwdTitle')" :visible.sync="passwordDialogVisible" width="420px">
      <div v-if="currentRow" class="dialog-tip">{{ $t('systemUsersPage.resetPwdHint', { username: currentRow.username }) }}</div>
      <el-input v-model.trim="passwordForm.password" show-password :placeholder="$t('systemUsersPage.newPwdPh')" />
      <span slot="footer">
        <el-button @click="passwordDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="passwordSaving" @click="savePassword">{{ $t('systemUsersPage.confirmReset') }}</el-button>
      </span>
    </form-dialog>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import FormDialog from '@/components/FormDialog.vue'
import {
  createSystemUser,
  deleteSystemUser,
  getSystemRoleOptions,
  getSystemUserRoleIds,
  getSystemUsers,
  resetSystemUserPassword,
  saveSystemUserRoles,
  updateSystemUser,
  updateSystemUserStatus
} from '@/api/system'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'

export default {
  name: 'SystemUsers',
  components: { ListPageShell, FormDialog },
  data() {
    return {
      loading: false,
      saving: false,
      roleSaving: false,
      passwordSaving: false,
      rows: [],
      total: 0,
      query: {
        pageNo: 1,
        pageSize: 10,
        keyword: '',
        statusCode: null
      },
      editDialogVisible: false,
      roleDialogVisible: false,
      passwordDialogVisible: false,
      editingId: null,
      currentRow: null,
      roleOptions: [],
      selectedRoleIds: [],
      passwordForm: {
        password: ''
      },
      userForm: {
        username: '',
        password: '',
        realName: '',
        email: '',
        phone: '',
        statusCode: 1
      }
    }
  },
  computed: {
    userRules() {
      return {
        username: [{ required: true, message: this.$t('systemUsersPage.rulesUsername'), trigger: 'blur' }],
        password: [{ min: 0, max: 64, message: this.$t('systemUsersPage.rulesPwdLen'), trigger: 'blur' }],
        realName: [{ required: true, message: this.$t('systemUsersPage.rulesRealName'), trigger: 'blur' }]
      }
    }
  },
  async created() {
    await this.$store.dispatch('fetchUserInfo')
    await Promise.all([this.load(), this.loadRoleOptions()])
  },
  methods: {
    formatDisplayDateTime,
    hasPermission(code) {
      const permissions = (this.$store.state.userInfo && this.$store.state.userInfo.permissions) || []
      const roleCodes = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
      return permissions.includes(code) || roleCodes.includes(code)
    },
    isCurrentUser(row) {
      return row && this.$store.state.userInfo && row.id === this.$store.state.userInfo.userId
    },
    roleText(row) {
      return ((row.roleNames || []).join(' · ') || (row.roleCodes || []).join(' · ')) || '—'
    },
    async load() {
      this.loading = true
      try {
        const resp = await getSystemUsers(this.query)
        const data = resp.data || {}
        this.rows = data.records || []
        this.total = data.total || 0
      } finally {
        this.loading = false
      }
    },
    async loadRoleOptions() {
      const resp = await getSystemRoleOptions()
      this.roleOptions = resp.data || []
    },
    handleSearch() {
      this.query.pageNo = 1
      this.load()
    },
    handleReset() {
      this.query = { pageNo: 1, pageSize: 10, keyword: '', statusCode: null }
      this.load()
    },
    resetUserForm() {
      this.userForm = {
        username: '',
        password: '',
        realName: '',
        email: '',
        phone: '',
        statusCode: 1
      }
      this.$nextTick(() => {
        if (this.$refs.userForm) this.$refs.userForm.clearValidate()
      })
    },
    openCreate() {
      this.editingId = null
      this.resetUserForm()
      this.editDialogVisible = true
    },
    openEdit(row) {
      this.editingId = row.id
      this.userForm = {
        username: row.username || '',
        password: '',
        realName: row.realName || '',
        email: row.email || '',
        phone: row.phone || '',
        statusCode: row.statusCode || 1
      }
      this.editDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.userForm) this.$refs.userForm.clearValidate()
      })
    },
    saveUser() {
      this.$refs.userForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            username: this.userForm.username,
            password: this.userForm.password,
            realName: this.userForm.realName,
            email: this.userForm.email,
            phone: this.userForm.phone,
            statusCode: this.userForm.statusCode
          }
          if (this.editingId) {
            await updateSystemUser(this.editingId, payload)
          } else {
            await createSystemUser(payload)
          }
          this.$message.success(this.editingId ? this.$t('systemUsersPage.updated') : this.$t('systemUsersPage.created'))
          this.editDialogVisible = false
          if (this.editingId && this.isCurrentUser({ id: this.editingId })) {
            await this.$store.dispatch('fetchUserInfo')
          }
          await this.load()
        } catch (e) {
          this.$message.error((e && e.message) || this.$t('systemUsersPage.saveFailed'))
        } finally {
          this.saving = false
        }
      })
    },
    async toggleStatus(row, value) {
      const previous = row.statusCode
      row.statusCode = value
      try {
        await updateSystemUserStatus(row.id, value)
        this.$message.success(this.$t('systemUsersPage.statusUpdated'))
      } catch (e) {
        row.statusCode = previous
        this.$message.error((e && e.message) || this.$t('systemUsersPage.statusFailed'))
      }
    },
    async openRoleDialog(row) {
      this.currentRow = row
      this.roleDialogVisible = true
      const resp = await getSystemUserRoleIds(row.id)
      this.selectedRoleIds = resp.data || []
    },
    async saveRoles() {
      if (!this.currentRow) return
      this.roleSaving = true
      try {
        await saveSystemUserRoles(this.currentRow.id, this.selectedRoleIds)
        this.$message.success(this.$t('systemUsersPage.rolesSaved'))
        this.roleDialogVisible = false
        if (this.isCurrentUser(this.currentRow)) {
          await this.$store.dispatch('fetchUserInfo')
        }
        await this.load()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('systemUsersPage.saveFailed'))
      } finally {
        this.roleSaving = false
      }
    },
    openPasswordDialog(row) {
      this.currentRow = row
      this.passwordForm.password = ''
      this.passwordDialogVisible = true
    },
    async savePassword() {
      if (!this.currentRow) return
      if (!this.passwordForm.password || this.passwordForm.password.length < 6) {
        this.$message.warning(this.$t('systemUsersPage.pwdTooShort'))
        return
      }
      this.passwordSaving = true
      try {
        await resetSystemUserPassword(this.currentRow.id, this.passwordForm.password)
        this.$message.success(this.$t('systemUsersPage.pwdReset'))
        this.passwordDialogVisible = false
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('systemUsersPage.pwdFailed'))
      } finally {
        this.passwordSaving = false
      }
    },
    handleDelete(row) {
      this.$confirm(this.$t('systemUsersPage.confirmDelete', { name: row.username }), this.$t('common.tip'), { type: 'warning' })
        .then(async () => {
          await deleteSystemUser(row.id)
          this.$message.success(this.$t('systemUsersPage.deleted'))
          if (this.rows.length === 1 && this.query.pageNo > 1) {
            this.query.pageNo -= 1
          }
          await this.load()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.filter-form {
  display: flex;
  flex-wrap: wrap;
}

.pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.danger-text {
  color: #f56c6c;
}

.dialog-tip {
  margin-bottom: 12px;
  color: var(--dark-text-sub, #8b95a1);
  font-size: 13px;
}

.role-check-group {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 8px;
}

.role-code {
  color: var(--dark-text-sub, #8b95a1);
  margin-left: 4px;
}
</style>
